import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Gift, Clock, Check, X, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { api } from '../services/api';

const STATUS_CONFIG = {
  reservado: { label: 'Reservado', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  utilizado: { label: 'Utilizado', color: 'bg-green-100 text-green-700 border-green-200', icon: Check },
  expirado:  { label: 'Expirado',  color: 'bg-gray-100 text-gray-500 border-gray-200', icon: X },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600 border-red-200', icon: X },
};

export default function MinhaCarteiraCupons() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get('/v2/vouchers/my-wallet')
      .then(res => setVouchers(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="animate-pulse bg-gray-50 rounded-xl h-20" />;
  }

  if (vouchers.length === 0) return null;

  const active = vouchers.filter(v => v.status === 'reservado');
  const used = vouchers.filter(v => v.status !== 'reservado');

  return (
    <div className="space-y-4" data-testid="minha-carteira">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5 text-[#56007F]" />
        <h3 className="text-lg font-bold">Meus Cupons</h3>
        <Badge variant="outline" className="text-xs">{vouchers.length}</Badge>
      </div>

      {active.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ativos</p>
          {active.map(v => (
            <VoucherCard key={v.voucher_id} voucher={v} expanded={expanded === v.voucher_id} onToggle={() => setExpanded(expanded === v.voucher_id ? null : v.voucher_id)} />
          ))}
        </div>
      )}

      {used.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Histórico</p>
          {used.map(v => (
            <VoucherCard key={v.voucher_id} voucher={v} expanded={expanded === v.voucher_id} onToggle={() => setExpanded(expanded === v.voucher_id ? null : v.voucher_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function VoucherCard({ voucher, expanded, onToggle }) {
  const v = voucher;
  const cfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.reservado;
  const StatusIcon = cfg.icon;
  const isActive = v.status === 'reservado';

  return (
    <Card className={`transition-all ${isActive ? 'border-amber-200' : 'opacity-70'}`} data-testid={`voucher-${v.voucher_code}`}>
      <CardContent className="p-0">
        <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50/50 transition-colors">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
            <Tag className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{v.coupon_titulo}</p>
            <p className="text-xs text-muted-foreground">{v.business_name}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] gap-1 ${cfg.color}`}>
            <StatusIcon className="w-3 h-3" /> {cfg.label}
          </Badge>
          {isActive ? (expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />) : null}
        </button>

        {expanded && isActive && (
          <div className="px-3 pb-4 pt-1 border-t text-center space-y-3">
            <p className="text-xs text-muted-foreground">Mostre este QR Code ou código ao lojista</p>
            <div className="inline-block bg-white p-3 rounded-lg border shadow-sm">
              <QRCodeSVG value={`${window.location.origin}/validar-voucher/${v.voucher_code}`} size={120} />
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 inline-block">
              <p className="text-[10px] text-gray-400 uppercase">Código</p>
              <p className="text-xl font-black tracking-widest">{v.voucher_code}</p>
            </div>
            <p className="text-[11px] text-gray-400 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              Válido até {new Date(v.expires_at).toLocaleDateString('pt-BR')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
